import { LucideIcon } from "lucide-react"
import React from "react"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Skeleton } from "../ui/skeleton"

type StatCardProps = {
  title: string
  value: string | number
  description?: string
  icon: LucideIcon
  isLoading?: boolean
  color: "blue" | "green" | "purple" | "orange" | "red" | "indigo"
  variant?: "default" | "gradient"
}

const colorConfig = {
  blue: {
    border: "border-l-blue-500 border-l-3",
    gradient: "from-blue-50/50",
    iconBg: "bg-blue-100",
    iconColor: "text-blue-600",
    valueColor: "text-blue-700",
  },
  green: {
    border: "border-l-green-500 border-l-3",
    gradient: "from-green-50/50",
    iconBg: "bg-green-100",
    iconColor: "text-green-600",
    valueColor: "text-green-700",
  },
  purple: {
    border: "border-l-purple-500 border-l-3",
    gradient: "from-purple-50/50",
    iconBg: "bg-purple-100",
    iconColor: "text-purple-600",
    valueColor: "text-purple-700",
  },
  orange: {
    border: "border-l-orange-500 border-l-3",
    gradient: "from-orange-50/50",
    iconBg: "bg-orange-100",
    iconColor: "text-orange-600",
    valueColor: "text-orange-700",
  },
  red: {
    border: "border-l-red-500 border-l-3",
    gradient: "from-red-50/50",
    iconBg: "bg-red-100",
    iconColor: "text-red-600",
    valueColor: "text-red-700",
  },
  indigo: {
    border: "border-l-indigo-500 border-l-3",
    gradient: "from-indigo-50/50",
    iconBg: "bg-indigo-100",
    iconColor: "text-indigo-600",
    valueColor: "text-indigo-700",
  },
}

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  description,
  icon: Icon,
  isLoading = false,
  color,
  variant = "gradient",
}) => {
  const config = colorConfig[color]

  const cardClassName =
    variant === "gradient"
      ? `${config.border} bg-gradient-to-r ${config.gradient} to-transparent`
      : ""

  return (
    <Card className={cardClassName}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>{title}</CardTitle>
        <div className={`p-2 ${config.iconBg} rounded-lg`}>
          <Icon className={`h-4 w-4 ${config.iconColor}`} />
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-8 w-16 mb-1" />
        ) : (
          <div className={`text-2xl font-bold ${config.valueColor}`}>
            {value}
          </div>
        )}
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
      </CardContent>
    </Card>
  )
}
